import { useRef, useState } from "react";
import { Volume2, Pause, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { speakText } from "@/lib/api";

/**
 * Reads a block of text aloud via OpenAI TTS. Caches the generated mp3 per
 * instance so replays cost nothing.
 */
export const ListenButton = ({ text, voice = "onyx", label = "Listen", testId = "listen-button" }) => {
  const [state, setState] = useState("idle"); // idle | loading | playing
  const audioRef = useRef(null);

  const play = async () => {
    if (state === "playing") {
      audioRef.current?.pause();
      setState("idle");
      return;
    }
    if (audioRef.current) {
      audioRef.current.play();
      setState("playing");
      return;
    }
    setState("loading");
    try {
      const r = await speakText(text, voice);
      const audio = new Audio(`data:${r.mime_type};base64,${r.audio_base64}`);
      audio.onended = () => setState("idle");
      audio.onerror = () => { setState("idle"); toast.error("Playback failed"); };
      audioRef.current = audio;
      await audio.play();
      setState("playing");
    } catch (err) {
      setState("idle");
      toast.error(err?.response?.data?.detail || "Voice generation failed");
    }
  };

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={play}
      disabled={state === "loading" || !text}
      className="flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-[#a78bfa] hover:text-white border border-[#a78bfa]/40 hover:border-[#a78bfa] px-3 py-1.5 transition-colors disabled:opacity-50"
    >
      {state === "loading" ? <Loader2 className="h-3 w-3 animate-spin" />
        : state === "playing" ? <Pause className="h-3 w-3" />
        : <Volume2 className="h-3 w-3" />}
      {state === "playing" ? "Pause" : label}
    </button>
  );
};

export default ListenButton;
